import { useQuery } from '@tanstack/react-query';
import { Bot, Cog, RefreshCw, User } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { type ActivityEntry } from '@apm/contracts';
import { EmptyState, ErrorState, Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { collaborationApi, collaborationKeys } from '../api';
import { gitActivityLine } from '../git-activity';

const ACTOR_ICON = { user: User, ai: Bot, system: Cog } as const;

/**
 * Lịch sử thay đổi của task, đọc thẳng từ `event_log`.
 *
 * Không có bảng activity riêng — event đã ghi đủ ai / lúc nào / cũ→mới. Bình
 * luận và đính kèm nằm cùng aggregate `task` nên chúng hiện ở đây xen kẽ đúng
 * thứ tự thời gian với đổi trạng thái, không phải hai dòng thời gian rời nhau.
 */
export function ActivityFeed({ taskId }: { taskId: string }): ReactNode {
  const { t } = useTranslation();

  const feed = useQuery({
    queryKey: collaborationKeys.activity(taskId),
    queryFn: () => collaborationApi.activity(taskId),
  });

  if (feed.isPending) return <Skeleton className="h-40 w-full" />;
  if (feed.isError) {
    return <ErrorState message={errorMessage(feed.error, t)} onRetry={() => void feed.refetch()} />;
  }
  if (feed.data.entries.length === 0) {
    return <EmptyState title={t('collaboration.noActivity')} />;
  }

  return (
    <>
      <ol className="space-y-2">
        {feed.data.entries.map((entry) => {
          // Đổi trạng thái do luật git (APM-GIT-h): "Git chuyển sang Xong — MR !45 …".
          const git = gitActivityLine(entry, t);
          return (
            <li key={entry.id} className="flex gap-2 text-sm">
              <ActorBadge entry={entry} />
              <div className="min-w-0 flex-1">
                <div>
                  <span className="font-medium">{git?.actor ?? actorLabel(entry, t)}</span>{' '}
                  <span className="text-muted-foreground">
                    {/* Chưa dịch được thì hiện thẳng tên event: một dòng lịch sử
                        khó đọc vẫn hơn một dòng trống. */}
                    {git?.text ?? t(`activity.${entry.eventType}`, entry.eventType)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {new Date(entry.occurredAt).toLocaleString('vi-VN')}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {feed.data.hasMore && (
        <p className="mt-3 text-xs text-muted-foreground">{t('collaboration.moreActivity')}</p>
      )}
    </>
  );
}

/**
 * Ai đổi. Dòng đến từ một lượt đồng bộ (APM-SYNC-h, plan-project-sync §5.6)
 * nói "Đồng bộ từ AI Service" thay vì "Hệ thống": lượt kéo chạy dưới `system`
 * vì API ngoài không cho biết ai đổi, nhưng người đọc cần biết thay đổi **đến
 * từ đâu** — "Hệ thống" đọc như ChillPM tự sửa việc của họ.
 */
function actorLabel(entry: ActivityEntry, t: ReturnType<typeof useTranslation>['t']): string {
  if (entry.syncSource) return t('collaboration.syncActor', { source: entry.syncSource });
  return entry.actorName ?? t(`collaboration.actor.${entry.actorType}`);
}

function ActorBadge({ entry }: { entry: ActivityEntry }): ReactNode {
  const Icon = entry.syncSource ? RefreshCw : ACTOR_ICON[entry.actorType];
  return (
    <span
      className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted"
      aria-hidden
    >
      <Icon className="h-3 w-3" />
    </span>
  );
}
